/**
 * Behaviour — the MonoBehaviour-style base class that TypeScript script authors
 * extend. It is the ergonomic "programmable code" surface; it is NOT a data
 * component and NOT itself an {@link import('./IScriptExecutor').IScriptExecutor}.
 *
 * At runtime the `TypeScriptExecutor` (script phase) constructs a Behaviour, binds
 * it via {@link Behaviour.__bind}, applies inspector variable overrides, and adapts
 * its lifecycle hooks to the unified `IScriptExecutor` contract.
 *
 * Component access is token-based ({@link Behaviour.getComponent}) — the codegen
 * "reflection" primitive — never a runtime type walk, so it behaves identically under
 * V8 and (future) NativeAOT-C#.
 */

import type {
  CollisionInfo,
  ComponentToken,
  EntityHandle,
  IEngineContext,
  InputActionEvent,
  SpriteCaps,
  TimeCaps,
  TransformCaps,
  WorldCaps,
} from './IEngineContext';
import { NO_ENTITY } from './IEngineContext';
import type { EngineApi } from './EngineApi.generated';

export abstract class Behaviour {
  /**
   * The capability-scoped engine context. Injected via {@link Behaviour.__bind}
   * before any lifecycle hook runs.
   */
  protected ctx!: IEngineContext;

  /** The entity this behaviour is attached to. */
  protected get entity(): EntityHandle {
    return this.ctx?.entity ?? NO_ENTITY;
  }

  /**
   * @internal Called by the TypeScript executor before any lifecycle hook. Authors
   * should not call this.
   */
  __bind(ctx: IEngineContext): void {
    this.ctx = ctx;
  }

  // --------------------------------------------------------------------------
  // Lifecycle hooks — override as needed (default no-ops).
  // --------------------------------------------------------------------------

  onAwake(): void {}
  onEnable(): void {}
  onStart(): void {}
  onUpdate(_deltaTime: number): void {}
  onFixedUpdate(_fixedDeltaTime: number): void {}
  onLateUpdate(_deltaTime: number): void {}
  onDisable(): void {}
  onDestroy(): void {}

  // --------------------------------------------------------------------------
  // Physics collision/trigger hooks — delivered via the executor's `triggerEvent`
  // (same-name convention) when the host physics subsystem dispatches to this script.
  // Default no-ops; override as needed.
  // --------------------------------------------------------------------------

  onCollisionEnter(_info: CollisionInfo): void {}
  onCollisionStay(_info: CollisionInfo): void {}
  onCollisionExit(_info: CollisionInfo): void {}
  onTriggerEnter(_info: CollisionInfo): void {}
  onTriggerStay(_info: CollisionInfo): void {}
  onTriggerExit(_info: CollisionInfo): void {}

  // --------------------------------------------------------------------------
  // Input action events — delivered when this entity holds an `input.InputReceiver`
  // whose action maps are active. Fires on phase transitions (started/performed/
  // canceled), so a behaviour reacts to presses without polling bindings each frame.
  // Default no-op; override as needed.
  // --------------------------------------------------------------------------

  onInputAction(_event: InputActionEvent): void {}

  // --------------------------------------------------------------------------
  // Ergonomic accessors over the bound context.
  // --------------------------------------------------------------------------

  /**
   * The global-services surface — `this.engine.physics.raycast(...)`,
   * `this.engine.events.emit(...)`, etc. Assembled per runtime from the registered
   * capability modules (§2/§6). Optional namespaces may be absent depending on the runtime.
   */
  protected get engine(): EngineApi {
    return this.ctx.engine;
  }

  protected get transform(): TransformCaps {
    return this.ctx.transform;
  }
  /** The bound entity's sprite (color/alpha) — `this.sprite.setColor(0xRRGGBB)`. */
  protected get sprite(): SpriteCaps {
    return this.ctx.sprite;
  }
  protected get time(): TimeCaps {
    return this.ctx.time;
  }
  protected get world(): WorldCaps {
    return this.ctx.world;
  }

  /**
   * Token-based component access — the reflection answer surfaced to authors:
   * `const t = this.getComponent(Transform)` where `Transform` is a generated token.
   * Returns a typed View (hot/SoA components) or plain data (AoS), or `null`.
   */
  protected getComponent<T>(token: ComponentToken<T>): T | null {
    return this.ctx.world.getComponent(this.entity, token);
  }

  protected hasComponent(token: ComponentToken<unknown>): boolean {
    return this.ctx.world.hasComponent(this.entity, token);
  }
}
